import {
  SolutionStrategyTag,
  StrategyTag,
} from "@/design-system/Tags/SolutionTag/SolutionTag";
import { cn } from "@/lib/utils";
import { Trans, useTranslation } from "@repo/shared/i18n";
import { convertSBCPrice } from "@repo/shared/utils";
import { Clock } from "lucide-react";
import { ReactNode } from "react";

import { SolutionTags } from "./SolutionTags";
import { AlternativeRow } from "./alternativeRow";

/* Columns take their content's width, and only when the row runs out of room
   do they give way and the sub line ("12 transferências") truncates. */
const FIGURE_MIN_W = "min-w-16 flex-auto";

const Figure = ({
  label,
  price,
  muted = false,
  sub,
}: {
  label: string;
  /** Absent when the strategy found no solution: renders a dash. */
  price?: number;
  /** Greyed: context for the other figures, not the headline. */
  muted?: boolean;
  /** The count behind the coins, e.g. "8 players" under the club value. */
  sub?: ReactNode;
}) => (
  /* Every column the same width as the header's dense figures, so the values
     line up when scanning down the list even when one is "0". */
  <div className={cn("flex flex-col gap-0.5", FIGURE_MIN_W)}>
    <span className="text-[10px] font-bold uppercase tracking-wide text-neutral-500">
      {label}
    </span>
    <span
      className={cn(
        "text-base font-extrabold",
        muted ? "text-neutral-500" : "text-white",
      )}
    >
      {price === undefined ? "-" : convertSBCPrice(price)}
    </span>
    {sub !== undefined && (
      <span className="truncate text-[11px] text-neutral-500">{sub}</span>
    )}
  </div>
);

const TimeFigure = ({ minutes }: { minutes?: number }) => {
  const { t } = useTranslation("sbcs");
  return (
    <div className={cn("flex shrink-0 flex-col gap-0.5", FIGURE_MIN_W)}>
      <span className="text-[10px] font-bold uppercase tracking-wide text-neutral-500">
        {t("figures.time")}
      </span>
      <span className="flex items-center gap-1.5 whitespace-nowrap text-base font-extrabold text-neutral-500">
        <Clock size={13} />
        {minutes === undefined ? "-" : t("figures.estimate", { minutes })}
      </span>
    </div>
  );
};

const count = { b: <span className="font-bold text-neutral-500" /> };

/**
 * One alternative solution, described the same way the summary header
 * describes the one on screen: the figures, then the tags that name it.
 * Stacked one per row so they read down a single column.
 */
export const AlternativeOptionRow = ({
  row,
  isCurrent,
  onSelect,
  showTime = false,
}: {
  row: AlternativeRow;
  isCurrent: boolean;
  onSelect: () => void;
  showTime?: boolean;
}) => {
  const { t } = useTranslation("sbcs");
  const { facts } = row;

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={isCurrent}
      className={cn(
        "flex w-full flex-col gap-2 rounded-2xl border-[1.5px] p-3.5 text-left",
        isCurrent
          ? "border-streamline-current-border bg-streamline-current-surface"
          : "border-neutral-800 bg-streamline-raised hover:border-neutral-700",
      )}
    >
      <div className="flex items-start gap-4">
        <Figure
          label={t("figures.transferCost")}
          price={facts.transferCost}
          sub={
            <Trans
              ns="sbcs"
              i18nKey="figures.transferCount"
              count={facts.transferCount}
              components={count}
            />
          }
        />
        <Figure
          label={t("figures.clubValue")}
          price={facts.clubValue}
          sub={
            <Trans
              ns="sbcs"
              i18nKey="figures.playerCount"
              count={facts.ownedCount}
              components={count}
            />
          }
        />
        <Figure
          label={t("figures.totalValue")}
          price={facts.totalValue}
          sub={
            <Trans
              ns="sbcs"
              i18nKey="figures.playerCount"
              count={facts.playerCount}
              components={count}
            />
          }
        />
        {showTime && <TimeFigure minutes={facts.estimatedMinutes} />}
      </div>

      <SolutionTags
        tags={row.shownTags ?? row.tags}
        recommended={row.recommended}
      />
    </button>
  );
};

/**
 * A strategy the solver found nothing for: the figures blurred, with the
 * label naming the strategy laid over them.
 */
export const UnavailableOptionRow = ({
  tag,
  showTime = false,
}: {
  tag: SolutionStrategyTag;
  showTime?: boolean;
}) => {
  const { t } = useTranslation("sbcs");
  return (
    <div className="w-full rounded-2xl border-[1.5px] border-neutral-800 bg-streamline-raised p-3.5 text-left">
      <div className="relative">
        <span className="absolute inset-0 z-10 flex items-center justify-center text-sm font-extrabold text-neutral-300">
          {/* Inline text, so the pill follows the last word when the label
              wraps; the no-break space keeps that word and the pill together. */}
          <p className="text-center [&>span]:align-middle">
            {t("streamline.noSolution")}
            {" "}
            <StrategyTag tag={tag} />
          </p>
        </span>
        <div
          aria-hidden
          className="flex items-start gap-4 opacity-30 blur-[2px] select-none"
        >
          <Figure label={t("figures.transferCost")} sub="-" />
          <Figure label={t("figures.clubValue")} sub="-" />
          <Figure label={t("figures.totalValue")} sub="-" />
          {showTime && <TimeFigure />}
        </div>
      </div>
    </div>
  );
};
